import { useMemo } from "react";
import { pixelTheme } from "../../theme/pixel";
import { OpenRouterPicker } from "./OpenRouterPicker";

/** A canonical Claude Code model id (no `/`). The Python tool routes
 *  any of these through the bundled CLI so usage is billed against the
 *  user's plan rather than burning OpenRouter credits. */
export interface ModelPreset {
  id: string;
  label: string;
}

const SENTINEL_OPENROUTER = "__openrouter__";

/** Shared single-model picker used by /admin/generate (server-wide
 *  default) and the Model Compare tool's "extra OpenRouter model" row.
 *
 *  Two modes:
 *  - **Preset**: choose from `presets` (Claude Code ids).
 *  - **OpenRouter**: pick the OpenRouter sentinel and type any
 *    provider-prefixed id (`anthropic/claude-opus-4-7`,
 *    `openai/gpt-5`, `google/gemini-2.5-pro`). The convention that
 *    `/` ⇒ OpenRouter is enforced server-side; the picker is just
 *    the UX for it.
 */
export function ModelPicker({
  value,
  onChange,
  presets,
  disabled,
  placeholder = "anthropic/... openai/... google/...",
  size = "default",
}: {
  value: string;
  onChange: (next: string) => void;
  presets: ReadonlyArray<ModelPreset>;
  disabled?: boolean;
  placeholder?: string;
  size?: "default" | "small";
}) {
  const theme = pixelTheme;
  // The dropdown shows the matching preset id, or the OpenRouter
  // sentinel when `value` isn't a preset (i.e. it's free-text).
  const selectValue = useMemo(() => {
    const match = presets.find((p) => p.id === value);
    return match ? match.id : SENTINEL_OPENROUTER;
  }, [presets, value]);

  const inputBase: React.CSSProperties = {
    fontFamily: theme.fontDisplay,
    fontSize: size === "small" ? 10 : 11,
    letterSpacing: theme.letterSpacing,
    padding: size === "small" ? "4px 6px" : "6px 8px",
    background: theme.bgDeep,
    color: theme.ink,
    border: `2px solid ${theme.panelEdge}`,
    outline: "none",
  };

  const isOpenRouter = selectValue === SENTINEL_OPENROUTER;

  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
      <select
        value={selectValue}
        onChange={(e) => {
          const next = e.target.value;
          if (next === SENTINEL_OPENROUTER) {
            // Switching to OpenRouter mode: clear out a stale preset
            // so the free-text input is empty and obvious.
            onChange("");
          } else {
            onChange(next);
          }
        }}
        disabled={disabled}
        style={{ ...inputBase, cursor: disabled ? "not-allowed" : "pointer" }}
      >
        {presets.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
        <option value={SENTINEL_OPENROUTER}>OpenRouter (any model)</option>
      </select>
      {isOpenRouter && (
        <div style={{ minWidth: 360 }}>
          <OpenRouterPicker
            selected={value && value.includes("/") ? [value] : []}
            onChange={(next) => onChange(next[0] ?? "")}
            single
            disabled={disabled}
            placeholder={placeholder}
          />
        </div>
      )}
    </div>
  );
}

/** The Claude Code presets used by both the generate tab and the
 *  compare tool. Keep the list short — surfacing every Claude model
 *  invites mistakes; the four below are the production-relevant ones. */
export const CLAUDE_CODE_PRESETS: ReadonlyArray<ModelPreset> = [
  { id: "claude-opus-4-7", label: "Opus 4.7" },
  { id: "claude-opus-4-6", label: "Opus 4.6" },
  { id: "claude-sonnet-4-6", label: "Sonnet 4.6" },
  { id: "claude-haiku-4-5-20251001", label: "Haiku 4.5" },
];
